/**
 * STORED USER SETTINGS
 * ====================
 *
 * The Settings page writes its whole state into
 * `profiles.notification_preferences`, a JSONB column. This is the read side:
 * one place that knows the shape and the fallbacks, so the Settings page and
 * the screens that consume those settings cannot drift apart.
 */

import type { StoredUserSettings } from '@/types';

/** Review schedule used when the user has not saved one of their own. */
export const DEFAULT_REVIEW_INTERVALS = [7, 30, 90];

/** Confidence the new-decision slider starts at when nothing is saved. */
export const DEFAULT_CONFIDENCE_RATING = 7;

/** Tags offered on the new-decision form before the user defines their own. */
export const DEFAULT_CUSTOM_TAGS = ['product', 'tech', 'hiring', 'design', 'strategy'];

interface ProfileLike {
  notification_preferences?: unknown;
}

/** The settings blob off a profile row, or an empty one if it is missing. */
export function readStoredSettings(profile: ProfileLike | null | undefined): StoredUserSettings {
  const prefs = profile?.notification_preferences;
  if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) return {};
  return prefs as StoredUserSettings;
}

/**
 * The user's saved review schedule, or the default.
 *
 * `limit` is the plan's cap on review dates per decision. A saved schedule can
 * exceed it — the user may have saved it on a paid plan that has since
 * lapsed — so it is trimmed rather than passed through, which would let the
 * new-decision form start over the cap it then refuses to add to.
 */
export function getDefaultReviewIntervals(
  profile: ProfileLike | null | undefined,
  limit = Infinity,
): number[] {
  const saved = readStoredSettings(profile).defaultReviewIntervals;
  const intervals =
    Array.isArray(saved) && saved.length > 0
      ? saved.filter((n) => typeof n === 'number' && n > 0)
      : DEFAULT_REVIEW_INTERVALS;
  const usable = intervals.length > 0 ? intervals : DEFAULT_REVIEW_INTERVALS;
  return Number.isFinite(limit) ? usable.slice(0, limit) : [...usable];
}

/** The user's saved starting confidence, or the default. */
export function getDefaultConfidenceRating(profile: ProfileLike | null | undefined): number {
  const saved = readStoredSettings(profile).preferences?.defaultRating;
  if (typeof saved !== 'number' || saved < 1 || saved > 10) return DEFAULT_CONFIDENCE_RATING;
  return saved;
}

/** The tag shortcuts the user configured in Settings, or the defaults. */
export function getCustomTags(profile: ProfileLike | null | undefined): string[] {
  const saved = readStoredSettings(profile).customTags;
  if (!Array.isArray(saved)) return DEFAULT_CUSTOM_TAGS;
  const tags = saved.filter((t): t is string => typeof t === 'string' && t.length > 0);
  return tags.length > 0 ? tags : DEFAULT_CUSTOM_TAGS;
}
